import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { AdminUser, Me, Role } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { Dialog, useToast } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { Field, Panel } from "../../components/Layout";
import { EmptyState, QueryState, SkeletonBlock } from "../../components/States";
import { dateTime, ROLE_LABEL } from "../../lib/format";

interface Options { agencies: string[]; counties: string[]; roads: { road_uuid: string; road_code: string; road_name: string; agency: string; county: string }[] }

const ROLES: { value: Role; help: string }[] = [
  { value: "viewer", help: "Reads the roads they're given access to." },
  { value: "engineer", help: "Reviews AI findings and adds defects. Sees every road." },
  { value: "surveyor", help: "Captures surveys. Sees the roads they're given access to." },
  { value: "admin", help: "Everything, including users and system settings." },
];

function accessSummary(u: AdminUser) {
  if (u.role === "engineer" || u.role === "admin") return "All roads (role)";
  if (u.access.all_roads) return "All roads";
  const parts = [...u.access.agencies, ...u.access.counties];
  if (u.access.road_uuids.length) parts.push(`+${u.access.road_uuids.length} road${u.access.road_uuids.length === 1 ? "" : "s"}`);
  return parts.join(", ") || "No access";
}

function Checks({ id, label, options, value, onChange }: { id: string; label: string; options: string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }} className="stack">
      <legend className="field-label" style={{ marginBottom: 6 }}>{label}</legend>
      {options.length ? options.map((o) => (
        <label key={o} className="check" htmlFor={`${id}-${o}`}>
          <input id={`${id}-${o}`} type="checkbox" checked={value.includes(o)} onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((x) => x !== o))} />{o}
        </label>
      )) : <span className="muted">None recorded</span>}
    </fieldset>
  );
}

function UserEditor({ user, me, options, onClose }: { user: AdminUser; me: Me; options: Options; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [fullName, setFullName] = useState(user.full_name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<Role>(user.role);
  const [active, setActive] = useState(user.active);
  const [password, setPassword] = useState("");
  const [allRoads, setAllRoads] = useState(user.access.all_roads);
  const [agencies, setAgencies] = useState(user.access.agencies);
  const [counties, setCounties] = useState(user.access.counties);
  const [roads, setRoads] = useState(user.access.road_uuids);
  const [roadQ, setRoadQ] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFullName(user.full_name); setEmail(user.email); setRole(user.role); setActive(user.active); setPassword("");
    setAllRoads(user.access.all_roads); setAgencies(user.access.agencies); setCounties(user.access.counties); setRoads(user.access.road_uuids); setError(null);
  }, [user]);

  const scoped = role === "viewer" || role === "surveyor";
  const accessChanged = allRoads !== user.access.all_roads || agencies.join() !== user.access.agencies.join() || counties.join() !== user.access.counties.join() || roads.join() !== user.access.road_uuids.join();

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      if (fullName !== user.full_name) body.full_name = fullName;
      if (email !== user.email) body.email = email;
      if (role !== user.role) body.role = role;
      if (active !== user.active) body.active = active;
      if (password) body.password = password;
      if (Object.keys(body).length) await api(`/api/admin/users/${encodeURIComponent(user.username)}`, { method: "PATCH", json: body });
      if (scoped && accessChanged) {
        await api(`/api/admin/users/${encodeURIComponent(user.username)}/access`, { method: "PUT", json: { all_roads: allRoads, agencies, counties, road_uuids: roads } });
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); toast({ text: `Saved ${user.username}.` }); setPassword(""); },
    onError: (e) => setError(e instanceof Error ? e.message : "Changes could not be saved."),
  });

  const roadMatches = useMemo(() => {
    const t = roadQ.trim().toLowerCase();
    if (!t) return [];
    return options.roads.filter((r) => !roads.includes(r.road_uuid) && `${r.road_code} ${r.road_name} ${r.county}`.toLowerCase().includes(t)).slice(0, 8);
  }, [roadQ, options.roads, roads]);

  const self = user.username === me.username;

  return (
    <form className="stack" style={{ gap: 20 }} onSubmit={(e) => { e.preventDefault(); setError(null); save.mutate(); }}>
      <div className="row"><h3 style={{ fontSize: 18 }}>{user.username}</h3><span className="spacer" /><button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button></div>
      <div className="form-grid">
        <Field label="Full name" htmlFor="ue-name"><input id="ue-name" className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} /></Field>
        <Field label="Email" htmlFor="ue-email"><input id="ue-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
      </div>
      <Field label="Role" htmlFor="ue-role" hint={ROLES.find((r) => r.value === role)?.help}>
        <select id="ue-role" className="select" value={role} disabled={self} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => <option key={r.value} value={r.value}>{ROLE_LABEL[r.value]}</option>)}
        </select>
      </Field>
      <label className="switch"><input type="checkbox" checked={active} disabled={self} onChange={(e) => setActive(e.target.checked)} />Account active</label>

      {scoped ? (
        <fieldset className="panel" style={{ padding: 16, margin: 0 }}>
          <legend className="field-label" style={{ padding: "0 6px" }}>Road access</legend>
          <div className="stack" style={{ gap: 16 }}>
            <label className="switch"><input type="checkbox" checked={allRoads} onChange={(e) => setAllRoads(e.target.checked)} />All roads</label>
            {!allRoads ? (
              <>
                <div className="grid grid-2">
                  <Checks id="ue-ag" label="Agencies" options={options.agencies} value={agencies} onChange={setAgencies} />
                  <Checks id="ue-co" label="Counties" options={options.counties} value={counties} onChange={setCounties} />
                </div>
                <div className="stack">
                  <Field label="Extra roads" htmlFor="ue-road-q" hint="Roads outside the agencies and counties above.">
                    <input id="ue-road-q" className="input" placeholder="Search road code or name" value={roadQ} onChange={(e) => setRoadQ(e.target.value)} />
                  </Field>
                  {roadMatches.length ? (
                    <div className="stack" style={{ gap: 4 }}>
                      {roadMatches.map((r) => (
                        <button type="button" key={r.road_uuid} className="menu-item" onClick={() => { setRoads([...roads, r.road_uuid]); setRoadQ(""); }}>
                          <Icon name="plus" size={14} />{r.road_code} — {r.road_name} <span className="muted">· {r.county}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="tag-list">
                    {roads.map((id) => {
                      const r = options.roads.find((x) => x.road_uuid === id);
                      return <span className="tag" key={id}>{r ? `${r.road_code} ${r.road_name}` : id}<button type="button" aria-label="Remove road" onClick={() => setRoads(roads.filter((x) => x !== id))}>×</button></span>;
                    })}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </fieldset>
      ) : <p className="muted">{ROLE_LABEL[role]}s see every road.</p>}

      <Field label="Set a new password" htmlFor="ue-pw" hint="Leave empty to keep the current one. At least 8 characters. Signs the user out everywhere.">
        <input id="ue-pw" className="input" type="password" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={save.isPending}>Save changes</button></div>
      <p className="muted" style={{ fontSize: 13 }}>Changing the role, road access, password or active status asks for your password again.</p>
    </form>
  );
}

function NewUserDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (u: string) => void }) {
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api("/api/admin/users", { method: "POST", json: { username, full_name: fullName, email, role, password } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "users"] }); onCreated(username); setUsername(""); setFullName(""); setEmail(""); setPassword(""); },
    onError: (e) => setError(e instanceof Error ? e.message : "The user could not be created."),
  });
  return (
    <Dialog open={open} onClose={onClose} labelledBy="nu-title">
      <form className="dialog-body" onSubmit={(e) => { e.preventDefault(); setError(null); m.mutate(); }}>
        <h2 className="dialog-title" id="nu-title">Add a user</h2>
        <Field label="Username" htmlFor="nu-user" hint="Letters, numbers, dots, dashes."><input id="nu-user" className="input" required minLength={3} pattern="[A-Za-z0-9._-]+" value={username} onChange={(e) => setUsername(e.target.value)} /></Field>
        <Field label="Full name" htmlFor="nu-name"><input id="nu-name" className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} /></Field>
        <Field label="Email" htmlFor="nu-email"><input id="nu-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Role" htmlFor="nu-role" hint={ROLES.find((r) => r.value === role)?.help}>
          <select id="nu-role" className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r.value} value={r.value}>{ROLE_LABEL[r.value]}</option>)}</select>
        </Field>
        <Field label="Temporary password" htmlFor="nu-pw" hint="At least 8 characters. Share it with the user directly."><input id="nu-pw" className="input" type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {role === "viewer" || role === "surveyor" ? <p className="muted" style={{ fontSize: 13 }}>After adding, choose which roads they can see.</p> : null}
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        <div className="form-actions"><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={m.isPending}>Add user</button></div>
      </form>
    </Dialog>
  );
}

function AgencySettings() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "agency-settings"], queryFn: () => api<{ items: { agency: string; hide_unreviewed: boolean }[] }>("/api/admin/agency-settings") });
  const m = useMutation({
    mutationFn: ({ agency, hide }: { agency: string; hide: boolean }) => api(`/api/admin/agency-settings/${encodeURIComponent(agency)}`, { method: "PUT", json: { hide_unreviewed: hide } }),
    onSuccess: () => qc.invalidateQueries(),
  });
  return (
    <Panel title="Unreviewed AI findings for viewers" id="ad-agency">
      <p className="muted" style={{ marginBottom: 12 }}>Viewers see unreviewed AI findings marked as such, never counted in totals. Switch them off for an agency to show its viewers verified findings only.</p>
      <QueryState query={q} skeleton={<SkeletonBlock rows={3} />}>
        {(d) => d.items.length ? (
          <div className="stack">
            {d.items.map((a) => (
              <label key={a.agency} className="switch">
                <input type="checkbox" checked={!a.hide_unreviewed} disabled={m.isPending} onChange={(e) => m.mutate({ agency: a.agency, hide: !e.target.checked })} />
                {a.agency}: {a.hide_unreviewed ? "verified only" : "show unreviewed"}
              </label>
            ))}
          </div>
        ) : <EmptyState title="No agencies recorded" />}
      </QueryState>
    </Panel>
  );
}

export function UsersTab({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const selected = params.get("user");
  const [adding, setAdding] = useState(false);
  const toast = useToast();
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: () => api<{ items: AdminUser[] }>("/api/admin/users") });
  const options = useQuery({ queryKey: ["admin", "options"], queryFn: () => api<Options>("/api/admin/options"), staleTime: 5 * 60_000 });
  const sel = users.data?.items.find((u) => u.username === selected);

  return (
    <>
      <div className="split">
        <Panel title="Users" id="ad-users" flush actions={<button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}><Icon name="plus" />Add user</button>}>
          <QueryState query={users} skeleton={<div className="panel-body"><SkeletonBlock rows={5} /></div>}>
            {(d) => (
              <DataTable<AdminUser>
                rows={d.items}
                rowKey={(u) => u.username}
                selectedKey={selected}
                onRowClick={(u) => setParams({ user: u.username }, { replace: true })}
                caption="Users"
                columns={[
                  { key: "u", label: "User", sort: (u) => u.username, render: (u) => <>{u.full_name || u.username}<small className="muted" style={{ display: "block" }}>{u.username}</small></> },
                  { key: "r", label: "Role", sort: (u) => u.role, render: (u) => ROLE_LABEL[u.role] },
                  { key: "a", label: "Road access", render: (u) => <>{accessSummary(u)}<small className="muted" style={{ display: "block" }}>{u.visible_roads} road{u.visible_roads === 1 ? "" : "s"}</small></> },
                  { key: "s", label: "Status", sort: (u) => (u.active ? 1 : 0), render: (u) => <span className={`chip ${u.active ? "verified" : "rejected"}`}>{u.active ? "Active" : "Disabled"}</span> },
                  { key: "l", label: "Last sign-in", sort: (u) => u.last_login, render: (u) => dateTime(u.last_login) },
                ]}
              />
            )}
          </QueryState>
        </Panel>
        <Panel title="Edit user" id="ad-edit">
          {sel && options.data ? <UserEditor user={sel} me={me} options={options.data} onClose={() => setParams({}, { replace: true })} />
            : <EmptyState title="Select a user">Choose someone in the list to change their role, road access or password.</EmptyState>}
        </Panel>
      </div>
      <AgencySettings />
      <NewUserDialog open={adding} onClose={() => setAdding(false)} onCreated={(u) => { setAdding(false); toast({ text: `Added ${u}.` }); setParams({ user: u }, { replace: true }); }} />
    </>
  );
}
